import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../config/firebase";

// Types for message storage
export interface UserInquiry {
  id?: string;
  userId?: string; // Optional - for authenticated users
  userEmail?: string; // Optional - if provided
  message: string;
  response: string;
  intent: string;
  confidence: number;
  isDialogflowResponse: boolean; // true if from Dialogflow, false if fallback
  sessionId: string;
  timestamp: Timestamp;
  status: "pending" | "reviewed" | "resolved" | "archived";
  adminNotes?: string;
  adminResponse?: string;
  adminRespondedAt?: Timestamp;
  adminRespondedBy?: string;
  userAgent?: string;
  ipAddress?: string; // If available
  tags?: string[]; // For categorization
}

export interface MessageFilters {
  status?: string;
  intent?: string;
  isDialogflowResponse?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  searchTerm?: string;
}

class MessageService {
  private collectionName = "userInquiries";

  /**
   * Save a user inquiry to the database
   */
  async saveInquiry(
    inquiry: Omit<UserInquiry, "id" | "timestamp">
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...inquiry,
        timestamp: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      console.error("Error saving user inquiry:", error);
      throw new Error("Failed to save inquiry");
    }
  }

  /**
   * Get all inquiries with optional filtering
   */
  async getInquiries(filters: MessageFilters = {}): Promise<UserInquiry[]> {
    try {
      // Start with base query - only order by timestamp to avoid index issues
      let q = query(
        collection(db, this.collectionName),
        orderBy("timestamp", "desc")
      );

      // Only apply server-side filters that don't require composite indexes
      if (filters.intent) {
        q = query(q, where("intent", "==", filters.intent));
      }

      if (filters.isDialogflowResponse !== undefined) {
        q = query(
          q,
          where("isDialogflowResponse", "==", filters.isDialogflowResponse)
        );
      }

      const querySnapshot = await getDocs(q);
      const inquiries: UserInquiry[] = [];

      querySnapshot.forEach((doc) => {
        const inquiryData = {
          id: doc.id,
          ...doc.data(),
        } as UserInquiry;
        inquiries.push(inquiryData);
      });

      // Apply client-side filters for status and other fields
      let filteredInquiries = inquiries;

      // Apply status filter on client side to avoid index issues
      if (filters.status) {
        filteredInquiries = filteredInquiries.filter(
          (inquiry) => inquiry.status === filters.status
        );
      }

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredInquiries = filteredInquiries.filter(
          (inquiry) =>
            inquiry.message.toLowerCase().includes(searchLower) ||
            inquiry.response.toLowerCase().includes(searchLower) ||
            inquiry.intent.toLowerCase().includes(searchLower)
        );
      }

      if (filters.dateFrom) {
        filteredInquiries = filteredInquiries.filter(
          (inquiry) => inquiry.timestamp.toDate() >= filters.dateFrom!
        );
      }

      if (filters.dateTo) {
        filteredInquiries = filteredInquiries.filter(
          (inquiry) => inquiry.timestamp.toDate() <= filters.dateTo!
        );
      }

      return filteredInquiries;
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      throw new Error("Failed to fetch inquiries");
    }
  }

  /**
   * Get inquiries that need admin attention
   */
  async getPendingInquiries(): Promise<UserInquiry[]> {
    return this.getInquiries({ status: "pending" });
  }

  /**
   * Get fallback inquiries (not handled by Dialogflow)
   */
  async getFallbackInquiries(): Promise<UserInquiry[]> {
    return this.getInquiries({ isDialogflowResponse: false });
  }

  /**
   * Update inquiry status and add admin response
   */
  async updateInquiry(
    inquiryId: string,
    updates: {
      status?: "pending" | "reviewed" | "resolved" | "archived";
      adminNotes?: string;
      adminResponse?: string;
      adminRespondedBy?: string;
      tags?: string[];
    }
  ): Promise<void> {
    try {
      const inquiryRef = doc(db, this.collectionName, inquiryId);

      const updateData: any = { ...updates };

      if (updates.adminResponse || updates.adminNotes) {
        updateData.adminRespondedAt = serverTimestamp();
      }

      await updateDoc(inquiryRef, updateData);
    } catch (error) {
      console.error("Error updating inquiry:", error);
      throw new Error("Failed to update inquiry");
    }
  }

  /**
   * Get inquiry statistics for admin dashboard
   */
  async getInquiryStats(): Promise<{
    total: number;
    pending: number;
    resolved: number;
    fallbackCount: number;
    dialogflowCount: number;
    byIntent: Record<string, number>;
  }> {
    try {
      const allInquiries = await this.getInquiries();

      const stats = {
        total: allInquiries.length,
        pending: allInquiries.filter((i) => i.status === "pending").length,
        resolved: allInquiries.filter((i) => i.status === "resolved").length,
        fallbackCount: allInquiries.filter((i) => !i.isDialogflowResponse)
          .length,
        dialogflowCount: allInquiries.filter((i) => i.isDialogflowResponse)
          .length,
        byIntent: {} as Record<string, number>,
      };

      // Count by intent
      allInquiries.forEach((inquiry) => {
        stats.byIntent[inquiry.intent] =
          (stats.byIntent[inquiry.intent] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error("Error getting inquiry stats:", error);
      throw new Error("Failed to get inquiry statistics");
    }
  }

  /**
   * Mark inquiry as resolved
   */
  async resolveInquiry(
    inquiryId: string,
    adminResponse: string,
    adminRespondedBy: string,
    adminNotes?: string
  ): Promise<void> {
    await this.updateInquiry(inquiryId, {
      status: "resolved",
      adminResponse,
      adminRespondedBy,
      adminNotes,
    });
  }

  /**
   * Archive old inquiries (cleanup)
   */
  async archiveOldInquiries(daysOld: number = 90): Promise<number> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const oldInquiries = await this.getInquiries({
        dateTo: cutoffDate,
      });

      let archivedCount = 0;
      for (const inquiry of oldInquiries) {
        if (inquiry.id && inquiry.status === "resolved") {
          await this.updateInquiry(inquiry.id, { status: "archived" });
          archivedCount++;
        }
      }

      return archivedCount;
    } catch (error) {
      console.error("Error archiving old inquiries:", error);
      throw new Error("Failed to archive old inquiries");
    }
  }
}

// Export singleton instance
export const messageService = new MessageService();
export default MessageService;
